import { HTMLAttributes, useContext } from 'react'

import { Button, Text, theme } from '@hhgtech/hhg-components'
import { TextArea } from '@hhgtech/hhg-components/mantine'
import { useForm, yupResolver } from '@mantine/form'
import { useIntl } from 'react-intl'
import { shallow } from 'zustand/shallow'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import { getHelloSitesUrl } from 'api/utils/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { ReactionType } from 'interfaces/types'
import { AppContext } from 'state/appContext'
import { useArticleStore } from 'state/article'

import { FeedbackStatus } from '.'
import { schema } from './helpers'
import { StyledFeedbackForm } from './index.styled'
import { showNotification } from '../notificationsHandler'

type FeedbackFormField = {
  status?: number
  message?: string
}
export const FeedbackForm = ({
  onSubmit: _onSubmit,
  ...rest
}: {
  onSubmit: () => void
} & HTMLAttributes<HTMLFormElement>) => {
  const { formatMessage: f } = useIntl()
  const {
    state: { isLoggedIn },
  } = useContext(AppContext)
  const { currentArticle, setIsDisliked } = useArticleStore(
    (state) => ({
      currentArticle: state.currentArticle,
      setIsDisliked: state.setIsDisliked,
    }),
    shallow,
  )
  const isMobile = useIsMobile()
  const { locale } = useRouterExtend()
  const form = useForm<FeedbackFormField>({
    initialValues: {
      message: '',
      status: FeedbackStatus.Other,
    },
    validate: yupResolver(schema),
  })

  const onSubmit = async (values: typeof form.values) => {
    if (!isLoggedIn) {
      redirectToSSOWithReturnUrl(
        `${getHelloSitesUrl(locale)}${currentArticle?.permalink}?feedback=${
          ReactionType.DisLike
        }&feedback_stat=${values.status}&message=${values.message}`,
        'reaction',
      )
      return
    }
    const dislikeResponse = await callApiNew(
      PATHS.SSO.SSO_FEEDBACK,
      {
        method: 'POST',
        data: {
          post_id: currentArticle?.id,
          feedback: ReactionType.DisLike,
          feedback_stat: values.status,
          message: values.message,
        },
      },
      'sso',
    )

    if (dislikeResponse?._data?.total) {
      setIsDisliked(true)
    }
    _onSubmit()
  }

  const onError = (fieldErrors: typeof form.errors) => {
    Object.values(fieldErrors).forEach((error) =>
      showNotification({
        type: 'danger',
        message: (error as string) || '',
        title: f({
          id: 'babyGrowth.error.title',
          defaultMessage: 'Invalid data',
        }),
      }),
    )
  }
  return (
    <StyledFeedbackForm onSubmit={form.onSubmit(onSubmit, onError)} {...rest}>
      <div
        // role='button'
        className={`feedback-status ${
          form.values.status === FeedbackStatus.Inaccurate ? 'active' : ''
        }`}
        onClick={() => {
          form.setFieldValue('status', FeedbackStatus.Inaccurate)
        }}
      >
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14 24C8.48 24 4 19.52 4 14C4 8.48 8.48 4 14 4C19.52 4 24 8.48 24 14C24 19.52 19.52 24 14 24ZM14 9C13.45 9 13 9.45 13 10V14C13 14.55 13.45 15 14 15C14.55 15 15 14.55 15 14V10C15 9.45 14.55 9 14 9ZM13 17H15V19H13V17Z"
            fill="#FE921D"
          />
        </svg>
        <Text size="p3" color={theme.colors.gray800}>
          {f({
            id: 'feedback.status.inaccurate',
            defaultMessage: 'This article contains incorrect information',
          })}
        </Text>
      </div>
      <div
        // role='button'
        className={`feedback-status ${
          form.values.status === FeedbackStatus.NoRelated ? 'active' : ''
        }`}
        onClick={() => {
          form.setFieldValue('status', FeedbackStatus.NoRelated)
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M11.6777 21.9957C11.2556 21.9571 10.8798 21.6495 10.7793 21.2047L10.2246 18.7594L7.51367 15.7477C7.18267 15.3807 7 14.9038 7 14.4098V5.17739C7 4.07239 7.895 3.17739 9 3.17739H17.0312C17.7723 3.17739 18.4431 3.61367 18.7441 4.29067L21.7461 11.0465C21.9131 11.4205 22 11.826 22 12.236V13.1774C22 14.2774 21.1 15.1774 20 15.1774H13C13 15.1774 14 17.5481 14 19.3141C14 20.9271 12.9624 21.6331 12.1074 21.9411C11.9647 21.9928 11.8184 22.0086 11.6777 21.9957ZM3.5 15.1774C2.672 15.1774 2 14.5054 2 13.6774V4.67739C2 3.84939 2.672 3.17739 3.5 3.17739C4.328 3.17739 5 3.84939 5 4.67739V13.6774C5 14.5054 4.328 15.1774 3.5 15.1774Z"
            fill="#2D87F3"
          />
        </svg>

        <Text size="p3" color={theme.colors.gray800}>
          {f({
            id: 'feedback.status.noRelated',
            defaultMessage:
              "This article doesn't have the information I'm looking for",
          })}
        </Text>
      </div>
      <TextArea
        w="100%"
        placeholder={f({
          id: 'feedback.message.placeholder',
          defaultMessage: 'Please tell us what was incorrect',
        })}
        {...form.getInputProps('message')}
      />
      <div className="feedback-disclaimer">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          style={{ width: '32px' }}
        >
          <path
            d="M14.8256 12.3007L8.96093 2.544C8.75626 2.20333 8.39693 2 7.9996 2C7.60226 2 7.24293 2.20333 7.03893 2.54333L1.1736 12.3007C0.965597 12.6467 0.960264 13.0793 1.15893 13.4307C1.35693 13.782 1.73093 14 2.13493 14H13.8649C14.2683 14 14.6429 13.782 14.8409 13.4307C15.0396 13.0793 15.0336 12.6467 14.8256 12.3007ZM8.33293 12H7.66626C7.48226 12 7.33293 11.8507 7.33293 11.6667V11C7.33293 10.816 7.48226 10.6667 7.66626 10.6667H8.33293C8.51693 10.6667 8.66626 10.816 8.66626 11V11.6667C8.66626 11.8507 8.51693 12 8.33293 12ZM7.9996 9.33333C7.6316 9.33333 7.33293 9.03467 7.33293 8.66667V6.66667C7.33293 6.29867 7.6316 6 7.9996 6C8.3676 6 8.66626 6.29867 8.66626 6.66667V8.66667C8.66626 9.03467 8.3676 9.33333 7.9996 9.33333Z"
            fill="#FE921D"
          />
        </svg>
        <Text size="n1" color={theme.colors.gray600}>
          {f({
            id: 'articlePage.disclaimer',
            defaultMessage:
              'Hello Health Group does not provide medical advice, diagnosis or treatment.',
          })}
        </Text>
      </div>

      <Button size="lg" color="primary" isBlock={isMobile}>
        {f({
          id: 'feedback.submit',
          defaultMessage: 'Submit',
        })}
      </Button>
    </StyledFeedbackForm>
  )
}
